import { Data } from 'signup-constants/signupData/Data';
import { companyInfo } from '../http-company-info/CompanyInfoClient';
import { handleCompanyInfoError, handleCompanyInfoSuccess } from '../http-company-info/handleCompanyInfoResponse';
import { getIsSuccessfullyVerified, getVerificationType } from '../store/app/selectors';
import { isStepInNextOrCurrentStep } from '../store/flow/selectors';
import { VisitorContextActionTypes } from '../store/visitor-context/action-types';
import { Step } from 'signup-constants/Step';
import { fetchPublicVisitorContext } from '../visitor-context-fetch/fetchPublicVisitorContext';
import { handleVisitorContextSuccess } from '../visitor-context-fetch/handleVisitorContextResponse';
import { EndpointKey } from './EndpointKey';
import { FlowActionTypes } from '../store/flow/action-types';
import { hasEndpointBeenFetched } from '../store/http-responses/selectors';
import { getIsActiveAndAssignedToVariant } from '../store/experiments/selectors';
import { ExperimentKeys } from '../experiments/ExperimentKeys';
import { ExperimentParameterGroup } from '../experiments/ExperimentParameterGroup';
import { GTKMWithDomainFromEmail, getGTKMCompanyInfo, getGTKMCompanyInfoClient } from '../http-gtkm-company-info/GTKMCompanyInfoClient';
import { handleGTKMCompanyInfoError } from '../http-gtkm-company-info/handleGTKMCompanyInfoResponse';
import { handleGTKMCompanyInfoSuccess } from '../http-gtkm-company-info/handleGTKMCompanyInfoResponse';
import { getCurrentStep } from '../store/flow/selectors';
import { shouldOnlyFetchGTKMData } from '../http-gtkm-company-info/fetchOnlyGTKMDataChecks';
import { isExistingUser, isExistingUserExistingAccount } from '../app/verificationTypeSelectors';
import { handleACQ175GTKMCompanyInfoError, handleACQ175GTKMCompanyInfoSuccess } from '../http-gtkm-company-info/handleACQ175GTKMCompanyInfoResponse';
import { handleACQ177GTKMCompanyInfoError, handleACQ177GTKMCompanyInfoSuccess } from '../http-gtkm-company-info/handleACQ177GTKMCompanyInfoResponse';
import { isAssignedToGTKMExperiment } from '../utils/gtkm/isAssignedToGTKM';

// NOTE: This object was wrapped in a function to avoid problematic cycles when frontend-infra-core migrates to Webpack 5
export const getEndpointsPrefilling = () => ({
  [EndpointKey.CompanyInfo]: {
    getRequestData: () => [Data.Email],
    handleError: handleCompanyInfoError,
    handleSuccess: handleCompanyInfoSuccess,
    method: companyInfo,
    stateCheck: state => {
      const isAssignedToGTKM = isAssignedToGTKMExperiment(state);

      // Don't invoke the original endpoint for ACQ173 or ACQ175 or ACQ177
      if (isAssignedToGTKM) {
        return false;
      }
      const isVerificationTypeSet = Boolean(getVerificationType(state));
      return isVerificationTypeSet && isStepInNextOrCurrentStep(state, [Step.CompanyDomain, Step.CompanyName, Step.Industry]);
    }
  },
  [EndpointKey.VisitorContextFetch]: {
    handleSuccess: handleVisitorContextSuccess,
    method: fetchPublicVisitorContext,
    reduxActionType: VisitorContextActionTypes.Fetch
  },
  [EndpointKey.GTKMCompanyInfo]: {
    getRequestData: () => [Data.Email],
    handleError: handleGTKMCompanyInfoError,
    handleSuccess: handleGTKMCompanyInfoSuccess,
    method: getGTKMCompanyInfo,
    reduxActionType: FlowActionTypes.GoNextStep,
    stateCheck: state => {
      const isACQ0173VariantAorC = getIsActiveAndAssignedToVariant(state, ExperimentKeys.ACQ0173, [ExperimentParameterGroup.Variant_A, ExperimentParameterGroup.Variant_C]);
      if (!isACQ0173VariantAorC) {
        return false;
      }
      const currentStep = getCurrentStep(state);
      return currentStep === Step.LoadingPage && getIsSuccessfullyVerified(state);
    }
  },
  // GTKMWithEmailDomain is required for agentic flow when the endpoint above is not invoked
  [EndpointKey.GTKMWithDomainFromEmail]: {
    getRequestData: () => [Data.Email],
    reduxActionType: FlowActionTypes.GoNextStep,
    once: true,
    stateCheck: state => {
      const isSuccessfullyVerifiedOrExisting = getIsSuccessfullyVerified(state) || isExistingUser(state) || isExistingUserExistingAccount(state);
      return isSuccessfullyVerifiedOrExisting && shouldOnlyFetchGTKMData(state, EndpointKey.GTKMWithDomainFromEmail);
    },
    method: GTKMWithDomainFromEmail
  },
  [EndpointKey.GTKMCompanyInfoACQ175]: {
    getRequestData: () => [Data.Email],
    handleError: handleACQ175GTKMCompanyInfoError,
    handleSuccess: handleACQ175GTKMCompanyInfoSuccess,
    method: getGTKMCompanyInfoClient,
    reduxActionType: FlowActionTypes.GoNextStep,
    stateCheck: state => {
      // TODO: Update fetching logic when loading state is added
      const endpointHasBeenFetched = hasEndpointBeenFetched(state, EndpointKey.GTKMCompanyInfoACQ175);
      if (endpointHasBeenFetched) {
        return false;
      }
      const isACQ0175VariantA = getIsActiveAndAssignedToVariant(state, ExperimentKeys.ACQ0175, [ExperimentParameterGroup.Variant_A]);
      if (!isACQ0175VariantA) {
        return false;
      }
      return getIsSuccessfullyVerified(state);
    }
  },
  [EndpointKey.GTKMCompanyInfoACQ177]: {
    getRequestData: () => [Data.Email],
    handleError: handleACQ177GTKMCompanyInfoError,
    handleSuccess: handleACQ177GTKMCompanyInfoSuccess,
    method: getGTKMCompanyInfoClient,
    reduxActionType: FlowActionTypes.GoNextStep,
    stateCheck: state => {
      const endpointHasBeenFetched = hasEndpointBeenFetched(state, EndpointKey.GTKMCompanyInfoACQ177);
      if (endpointHasBeenFetched) {
        return false;
      }
      const isACQ0177VariantA = getIsActiveAndAssignedToVariant(state, ExperimentKeys.ACQ0177, [ExperimentParameterGroup.Variant_A]);

      // we hope to keep the logic of ACQ177 Variant A and ACQ175 Variant C totally the same
      const isACQ0175VariantC = getIsActiveAndAssignedToVariant(state, ExperimentKeys.ACQ0175, [ExperimentParameterGroup.Variant_C]);
      if (!isACQ0177VariantA && !isACQ0175VariantC) {
        return false;
      }
      return getIsSuccessfullyVerified(state);
    }
  }
});